import { Button, ButtonProps } from "@/components/ui/button";
import ImageSvg from "@/components/ui/image-svg";
import { clsx } from "clsx";
import { ArrowRight } from "lucide-react";
import { BsHourglassSplit } from "react-icons/bs";
import { Link } from "react-router-dom";

type ImageProps = {
  src: string;
  alt?: string;
};

type TimelineItem = {
  image: ImageProps;
  date: string;
  description: string;
};

type Props = {
  tagline: string;
  heading: string;
  description: string;
  buttons: ButtonProps[];
  timelineItems: TimelineItem[];
  stateName: string;
};

export type JourneySectionProps = React.ComponentPropsWithoutRef<"section"> &
  Partial<Props>;

export const JourneySection = (props: JourneySectionProps) => {
  const { tagline, timelineItems } = {
    ...JourneySectionDefaults,
    ...props,
  };

  return (
    <section
      id="section-journey"
      className="relative overflow-hidden  py-10 md:py-24"
    >
      <div className="container relative">
        <div className="absolute right-5 top-5 hidden lg:block">
          <Link to="/fazer-cotacao">
            <Button
              variant="ghost"
              className="border border-n-primary text-n-primary"
            >
              <BsHourglassSplit
                className="mr-2 h-6 w-6 animate-pulse "
                style={{ animationDuration: "2s" }}
              />{" "}
              Faça uma cotação em 1 minuto!
            </Button>
          </Link>
        </div>

        {/* Cabeçalho */}
        <div className="mb-12 md:mb-18 lg:mb-20">
          <div className=" w-full max-w-3xl text-start">
            <p className="mb-3 font-semibold md:mb-4">{tagline}</p>
            <h2 className="mb-5 text-4xl font-semibold text-n-primary md:mb-6 lg:text-5xl">
              Como contratar o transporte de veículos com a Nacional GO?
            </h2>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative grid auto-cols-fr grid-cols-1 md:flex">
          {timelineItems.map((item, index) => (
            <TimelineItem
              key={index}
              isFirstItem={index === 0}
              isLastItem={index === timelineItems.length - 1}
              {...item}
            />
          ))}
        </div>

        <div className="w-full flex items-center justify-center">
          <div className=" flex flex-col items-center justify-between gap-4 rounded-3xl md:rounded-full bg-white md:px-10 px-4 py-5 shadow-md md:mt-[60px] mt-16 md:flex-row w-fit">
            <h4
              className="text-center text-2xl text-n-primary md:text-[42px]"
              style={{ fontFamily: "'Anton', sans-serif", lineHeight: "1.2em" }}
            >
              Simule e contrate seu transporte online
            </h4>
            <Link to="/fazer-cotacao">
              <Button
                variant="default"
                className="rounded-full px-6"
                id="btn-fazer-cotacao"
              >
                Começar agora
                <ArrowRight className=" h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const TimelineItem = ({
  image,
  date,
  isFirstItem,
  isLastItem,
}: {
  image: ImageProps;
  date: string;
  isFirstItem: boolean;
  isLastItem: boolean;
}) => {
  return (
    <div className="relative flex flex-col items-center md:flex-1">
      {/* Imagem */}
      <div className="mb-4 flex h-48 items-center justify-center">
        <ImageSvg
          src={image.src}
          alt={image.alt}
          className="h-full w-auto object-contain"
        />
      </div>

      {/* Linha e ponto */}
      <div className="relative mb-4 flex w-full items-center">
        {isFirstItem && (
          <div className="absolute left-0 top-1/2 hidden h-[2px] w-8 -translate-y-1/2 bg-gradient-to-r from-gray-300 to-transparent md:block" />
        )}
        <div className="hidden h-[2px] flex-1 bg-n-primary md:block" />
        <div className="z-20 size-3 rounded-full bg-n-secondary border-2 border-n-primary shadow-[0_0_0_4px_#EDEFEC]" />
        <div
          className={clsx("hidden h-[2px] flex-1 bg-n-primary md:block", {
            hidden: isLastItem,
          })}
        />
        {isLastItem && (
          <div className="absolute right-0 top-1/2 hidden h-[2px] w-8 -translate-y-1/2 bg-gradient-to-l from-gray-300 to-transparent md:block" />
        )}
      </div>

      {/* Texto */}
      <div className="text-center">
        <h3 className="mb-2 text-xl font-semibold text-n-text-green">{date}</h3>
      </div>
    </div>
  );
};

export const JourneySectionDefaults: Props = {
  stateName: "Todo o Brasil",
  tagline: "",
  heading: "Como contratar o transporte de veículos com a Nacional GO?",
  description:
    "Contratar o transporte do seu carro, moto ou utilitário é simples e 100% online. Veja como fazer em poucos passos.",
  buttons: [
    { title: "Fazer cotação", variant: "default", id: "btn-fazer-cotacao" },
  ],
  timelineItems: [
    {
      image: {
        src: "humano-mulher.svg",
        alt: "Humano mulher",
      },
      date: "Faça seu orçamento online em instantes",
      description: "Informe origem, destino e detalhes do veículo.",
    },
    {
      image: {
        src: "escolha.svg",
        alt: "Escolha transporte",
      },
      date: "Escolha a melhor opção para você",
      description: "Selecione o tipo de transporte ideal para sua necessidade.",
    },
    {
      image: {
        src: "confirmacao-contrato-pagamento.svg",
        alt: "Contrato e pagamento",
      },
      date: "Confirme e pague online",
      description: "Tudo 100% online, com diferentes formas de pagamento.",
    },
    {
      image: {
        src: "mobile.svg",
        alt: "Acompanhamento",
      },
      date: "Acompanhe online seu transporte",
      description: "Rastreamento online e suporte da nossa equipe.",
    },
    {
      image: {
        src: "entrega-onde-quiser.svg",
        alt: "Entrega do veículo",
      },
      date: "Receba seu veículo no local combinado",
      description:
        "O veículo é entregue no local combinado, de forma segura e ágil.",
    },
  ],
};
